import { useDispatch, useSelector } from 'react-redux'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react'

// antd
import {
    Typography,
    Avatar,
    Button,
    Modal,
    Select,
    message,
    Dropdown,
    Popover,
    Tabs,
    Tooltip,
} from 'antd'
import { debounce } from 'lodash'
import { Icon } from '@iconify/react'

// slices
import {
    taskListSupport,
    taskById,
    mainTaskList,
    mainTaskById,
    tasksState,
} from '../taskRefactored/tasks.slice'
import {
    usersListForClientModal,
    usersState,
    resetClientModalUsers,
    setClientModalUserSearch,
} from '../users/users.slice'
import { loginState } from '../login/login.slice'
import {
    fetchProjectDetail,
    projectsState,
    updateProject,
} from './projects.slice'
import {
    clearWidgetSession,
    getWidgetProjectId,
    isMongoObjectId,
    refreshWidgetTokenFromApi,
    resolveWidgetCodeForRoute,
} from '../../helpers/widgetAuth'
import {
    assignUserToProject,
    unAssignUserToProject,
    getProjectByWidgetCode,
} from './projects.service'
import { getProjectPermissions } from '../clients/clients.service'
import {
    ProjectPermissionsContext,
    resolveModulePermission,
    PROJECT_MODULE_SUPPORT,
} from './projectPermissions'
import { loadDevelopmentSprintBoard } from './developmentSprint.slice'
import {
    buildDevelopmentSprintListQuery,
    buildMainTaskListRequest,
} from '../taskRefactored/tasks.service'
import { getClientsAccess } from '../../helpers/reportsPermission'

// components
import ProjectRoadMap from './ProjectRoadMap'
import ProjectTaskList from './ProjectTaskList'
import ProjectFiles from './components/ProjectFiles'
import ProjectStoryMap from './components/ProjectStoryMap'
import {
    STORY_MAP_TAB_BAR_HOST_ATTR,
    STORY_MAP_TAB_BAR_SLOT_ATTR,
} from './components/projectTabBarPortal'
import TaskDetailView from './components/TaskDetailView'
import ImportTasksModal from './components/ImportTasksModal'
import ProjectTaskToolbar from './components/ProjectTaskToolbar'
import CopyWidgetLinkButton from './components/CopyWidgetLinkButton'
import { showActionNotification } from '../../components/notification'
import ProjectColorIconFlyout from './components/ProjectColorIconFlyout'
import ProjectTaskListView from './components/listView/ProjectTaskListView'
import CreateProjectModal from './components/CreateProjectModal'
import CreateClientModal from '../clients/components/CreateClientModal'
import DevelopmentSprintView from './components/DevelopmentSprintView'
import ReportsView from './components/reports/ReportsView'
import ProjectAccessDeniedView from './components/ProjectAccessDeniedView'
import ProjectTabsScrollArrows from './components/ProjectTabsScrollArrows'
import TestRunMain from '../testRun'
import TestRunDetails from '../testRun/components/testRun/details/TestRunDetails'

// constants
import {
    PRIVATE_ROUTES,
    buildProjectTaskTabSearch,
    buildProjectWidgetSearch,
    isProjectWidgetMode,
    PROJECT_TASK_TAB_DEVELOPMENT,
    PROJECT_TASK_TAB_QUERY,
} from '../../routes'
import { DEFAULT_COLOR_PALETTE } from '../../constants/projects'
import { harmonicIconToneForBackground } from '../../helpers/harmonicIconColor'

// assets
import { CloseModalIcon, CrossIcon } from '../../assets/icons'
import { getRandomAvatar } from '../../assets/clients/avatar'

// css
import styles from './projectDetail.module.css'

const { Title, Text } = Typography
const SUPPORT_VIEW_ROUTES = ['board', 'list', 'roadmap', 'sprint']
const DEVELOPMENT_VIEW_ROUTES = ['board', 'list', 'roadmap', 'sprint']
const TOP_LEVEL_VIEW_ROUTES = ['files', 'reports', 'storyMap', 'testrun']

/** Merge toolbar filter updates and drop cleared fields (null / empty). */
const mergeToolbarFilter = (prev = {}, next = {}) => {
    const merged = { ...prev, ...next }

    if (Object.prototype.hasOwnProperty.call(next, 'type') && !next.type) {
        delete merged.type
    }
    if (
        Object.prototype.hasOwnProperty.call(next, 'selectedType') &&
        (!Array.isArray(next.selectedType) || next.selectedType.length === 0)
    ) {
        merged.selectedType = []
        delete merged.type
    }
    if (
        Object.prototype.hasOwnProperty.call(next, 'isCompleted') &&
        typeof next.isCompleted !== 'boolean'
    ) {
        delete merged.isCompleted
    }
    if (
        Object.prototype.hasOwnProperty.call(next, 'mineAssignee') &&
        !next.mineAssignee
    ) {
        delete merged.mineAssignee
    }

    return merged
}

const resolveProjectDetailRecord = (detail) => {
    if (!detail || typeof detail !== 'object') return null
    if (detail._id && detail.success !== true) return detail
    if (detail.result?._id) return detail.result
    return detail._id ? detail : null
}

const readStoryMapModulePermission = (detail) => {
    const record = resolveProjectDetailRecord(detail)
    const modulePermissions = record?.supportProjectPermission
    return modulePermissions?.storymap ?? modulePermissions?.storyMap ?? null
}

const readStoryMapPermissionFromListResponse = (data, projectId) => {
    const result = data?.result ?? data?.data
    if (!result) return null

    const items = Array.isArray(result) ? result : [result]
    const match = projectId
        ? items.find((item) => String(item?.projectId) === String(projectId))
        : items[0]

    const permissions = match?.permissions
    return permissions?.storymap ?? permissions?.storyMap ?? null
}

const readTestRunModulePermission = (detail) => {
    const record = resolveProjectDetailRecord(detail)
    const modulePermissions = record?.supportProjectPermission
    return modulePermissions?.test ?? modulePermissions?.testrun ?? null
}

const readTestRunPermissionFromListResponse = (data, projectId) => {
    const result = data?.result ?? data?.data
    if (!result) return null

    const items = Array.isArray(result) ? result : [result]
    const match = projectId
        ? items.find((item) => String(item?.projectId) === String(projectId))
        : items[0]

    const permissions = match?.permissions
    return permissions?.test ?? permissions?.testrun ?? null
}

const TASK_DETAIL_DRAWER_MIN_WIDTH = 380
const TASK_DETAIL_DRAWER_MAX_WIDTH = 900

const getDefaultTaskDetailDrawerWidth = () => {
    if (typeof window === 'undefined') return 560
    return Math.min(Math.max(Math.round(window.innerWidth * 0.44), 460), 700)
}

const copyTextToClipboard = async (text) => {
    if (!text) return false
    if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text)
        return true
    }
    const ta = document.createElement('textarea')
    ta.value = text
    ta.setAttribute('readonly', '')
    ta.style.position = 'fixed'
    ta.style.left = '-9999px'
    document.body.appendChild(ta)
    ta.select()
    try {
        return document.execCommand('copy')
    } finally {
        document.body.removeChild(ta)
    }
}

const readTaskTabQuery = (search) =>
    new URLSearchParams(search || '').get(PROJECT_TASK_TAB_QUERY)

const mergeProjectRouteSearch = (
    isWidgetMode,
    widgetSearch,
    extraSearch = ''
) => {
    if (!isWidgetMode) return extraSearch
    if (!extraSearch) return widgetSearch
    const params = new URLSearchParams(
        extraSearch.startsWith('?') ? extraSearch.slice(1) : extraSearch
    )
    params.set('mode', 'widget')
    return `?${params.toString()}`
}

const resolveProjectRouteSearch = (
    isWidgetMode,
    widgetSearch,
    locationSearch = ''
) => (isWidgetMode ? widgetSearch : locationSearch || '')

const ProjectDetail = () => {
    const { id: routeId, view, taskId } = useParams()
    const location = useLocation()
    const navigate = useNavigate()
    const dispatch = useDispatch()

    const isWidgetMode = useMemo(
        () => isProjectWidgetMode(location.search),
        [location.search]
    )
    const widgetSearch = isWidgetMode
        ? buildProjectWidgetSearch(
              resolveWidgetCodeForRoute(routeId, location.search)
          )
        : ''

    const { projectDetail, projectDetailStatus, projectAccessDeniedMessage } =
        useSelector(projectsState)

    const [widgetAuthStatus, setWidgetAuthStatus] = useState(() =>
        isProjectWidgetMode(
            typeof window !== 'undefined' ? window.location.search : ''
        )
            ? 'loading'
            : 'idle'
    )
    const [widgetResolvedProjectId, setWidgetResolvedProjectId] = useState(null)

    const projectId = useMemo(() => {
        if (!isWidgetMode) return routeId
        return (
            widgetResolvedProjectId ??
            getWidgetProjectId() ??
            (isMongoObjectId(routeId) ? routeId : null) ??
            projectDetail?._id ??
            null
        )
    }, [isWidgetMode, routeId, widgetResolvedProjectId, projectDetail?._id])
    const {
        projectTaskList,
        projectTaskListStatus,
        projectTaskListPage,
        projectTaskListIsNext,
        projectMainTaskList,
        projectMainTaskListStatus,
        projectMainTaskListPage,
        projectMainTaskListIsNext,
    } = useSelector(tasksState)
    const {
        clientModalUsersListData = [],
        clientModalUserPage,
        clientModalUserSearch,
        clientModalUserIsNext,
        getUserStatus,
    } = useSelector(usersState)

    const { userProfile, status: loginStatus } = useSelector(loginState)

    const [isMemberModalVisible, setIsMemberModalVisible] =
        React.useState(false)
    const [inviteEmail, setInviteEmail] = React.useState([])
    const [selectedMap, setSelectedMap] = React.useState({})
    const [isInviting, setIsInviting] = useState(false)
    const [userSearchValue, setUserSearchValue] = useState('')
    const [selectedTaskId, setSelectedTaskId] = useState(null)
    const selectedTaskIdRef = useRef(null)
    const [taskDetailVisible, setTaskDetailVisible] = useState(false)
    const [taskDetailFullscreen, setTaskDetailFullscreen] = useState(false)
    // 'idle' | 'entering' | 'exiting'
    const [fullscreenAnimState, setFullscreenAnimState] = useState('idle')
    const [scrollToSection, setScrollToSection] = useState(null)
    const [filteredAssignees] = useState([])
    const [taskListSearchTerm, setTaskListSearchTerm] = useState('')
    const [supportListQuery, setSupportListQuery] = useState({
        isCompleted: null,
        mineAssignee: null,
    })
    const taskDetailDrawerRef = useRef(null)
    const [projectMenuOpen, setProjectMenuOpen] = useState(false)
    const [colorIconPopoverOpen, setColorIconPopoverOpen] = useState(false)
    const [avatarColorIconPopoverOpen, setAvatarColorIconPopoverOpen] =
        useState(false)
    const [importModalVisible, setImportModalVisible] = useState(false)
    const [editProjectOpen, setEditProjectOpen] = useState(false)
    const [projectClientsPopoverOpen, setProjectClientsPopoverOpen] =
        useState(false)
    const [isClientModalOpen, setIsClientModalOpen] = useState(false)
    const [clientModalMode, setClientModalMode] = useState('create')
    const [editingClient, setEditingClient] = useState(null)
    const addTaskToFirstSwimlaneRef = useRef(null)
    const developmentToolbarFilterRef = useRef({})
    const supportToolbarFilterRef = useRef({})
    const [activeMainTab, setActiveMainTab] = useState('support')
    const [supportSubView, setSupportSubView] = useState('board')
    const [developmentSubView, setDevelopmentSubView] = useState('board')
    const [liveTaskTitlePatch, setLiveTaskTitlePatch] = useState(null)
    const [taskDetailDrawerWidth, setTaskDetailDrawerWidth] = useState(
        getDefaultTaskDetailDrawerWidth
    )
    const taskDetailMainContentRef = useRef(null)
    const taskDetailDrawerWidthRef = useRef(getDefaultTaskDetailDrawerWidth())
    const taskDetailResizeRef = useRef({ startX: 0, startWidth: 560 })
    const taskDetailResizeRafRef = useRef(null)
    const taskDetailPendingWidthRef = useRef(null)
    const canAccessDevelopmentTab = useMemo(
        () =>
            !isWidgetMode &&
            Boolean(userProfile?.isSuperAdmin || userProfile?.isExistingUser),
        [isWidgetMode, userProfile?.isSuperAdmin, userProfile?.isExistingUser]
    )

    const canCopyWidgetLink = useMemo(
        () =>
            !isWidgetMode &&
            Boolean(userProfile?.isSuperAdmin || userProfile?.isExistingUser),
        [isWidgetMode, userProfile?.isSuperAdmin, userProfile?.isExistingUser]
    )

    const canBypassStoryMapPermissions = useMemo(
        () => Boolean(userProfile?.isSuperAdmin),
        [userProfile?.isSuperAdmin]
    )

    const isClientUser = useMemo(
        () =>
            Boolean(
                userProfile?.isSupportUser &&
                !userProfile?.isExistingUser &&
                !userProfile?.isSuperAdmin
            ),
        [
            userProfile?.isSupportUser,
            userProfile?.isExistingUser,
            userProfile?.isSuperAdmin,
        ]
    )

    const canEditProjectClient = useMemo(() => {
        if (isWidgetMode || isClientUser) return false
        return getClientsAccess(userProfile).canEdit
    }, [isWidgetMode, isClientUser, userProfile])

    const projectModulePermissions = useMemo(() => {
        // Widget embeds are view-only (same as isRead without isEdit/isDelete).
        if (isWidgetMode) {
            return {
                [PROJECT_MODULE_SUPPORT]: {
                    isRead: true,
                    isEdit: false,
                    isDelete: false,
                },
            }
        }
        const record = resolveProjectDetailRecord(projectDetail)
        return record?.supportProjectPermission ?? null
    }, [isWidgetMode, projectDetail])

    const projectPermissionsContextValue = useMemo(
        () => ({
            // Never bypass permissions in widget mode — keep it read-only.
            isSuperAdmin: isWidgetMode
                ? false
                : Boolean(userProfile?.isSuperAdmin),
            modules: projectModulePermissions,
        }),
        [isWidgetMode, userProfile?.isSuperAdmin, projectModulePermissions]
    )

    const supportPermission = useMemo(
        () =>
            resolveModulePermission(
                PROJECT_MODULE_SUPPORT,
                projectPermissionsContextValue
            ),
        [projectPermissionsContextValue]
    )
    const canViewSupportTab = supportPermission.canView

    const [clientStoryMapPermission, setClientStoryMapPermission] =
        useState(null)
    const [clientTestRunPermission, setClientTestRunPermission] = useState(null)
    const [
        clientStoryMapPermissionsResolved,
        setClientStoryMapPermissionsResolved,
    ] = useState(false)

    const storyMapPermissionFromDetail = useMemo(
        () => readStoryMapModulePermission(projectDetail),
        [projectDetail]
    )

    const testRunPermissionFromDetail = useMemo(
        () => readTestRunModulePermission(projectDetail),
        [projectDetail]
    )

    const storyMapPermission =
        storyMapPermissionFromDetail ?? clientStoryMapPermission

    const testRunPermission =
        testRunPermissionFromDetail ?? clientTestRunPermission

    useEffect(() => {
        setClientStoryMapPermissionsResolved(false)
        setClientStoryMapPermission(null)
        setClientTestRunPermission(null)
    }, [projectId])

    useEffect(() => {
        if (canBypassStoryMapPermissions || !isClientUser || !projectId) {
            setClientStoryMapPermission(null)
            setClientTestRunPermission(null)
            setClientStoryMapPermissionsResolved(true)
            return undefined
        }

        const needsStoryMapFetch = !storyMapPermissionFromDetail
        const needsTestRunFetch = !testRunPermissionFromDetail

        if (!needsStoryMapFetch && !needsTestRunFetch) {
            setClientStoryMapPermission(null)
            setClientTestRunPermission(null)
            setClientStoryMapPermissionsResolved(true)
            return undefined
        }

        if (projectDetailStatus !== 'loaded') {
            setClientStoryMapPermissionsResolved(false)
            return undefined
        }

        const supportUserId = userProfile?._id
        if (!supportUserId) {
            setClientStoryMapPermissionsResolved(false)
            return undefined
        }

        let cancelled = false

        ;(async () => {
            try {
                const { data } = await getProjectPermissions(
                    supportUserId,
                    projectId
                )
                if (cancelled) return

                if (needsStoryMapFetch) {
                    const permission = readStoryMapPermissionFromListResponse(
                        data,
                        projectId
                    )
                    setClientStoryMapPermission(permission)
                }

                if (needsTestRunFetch) {
                    const permission = readTestRunPermissionFromListResponse(
                        data,
                        projectId
                    )
                    setClientTestRunPermission(permission)
                }
            } finally {
                if (!cancelled) {
                    setClientStoryMapPermissionsResolved(true)
                }
            }
        })()

        return () => {
            cancelled = true
        }
    }, [
        canBypassStoryMapPermissions,
        isClientUser,
        projectId,
        projectDetailStatus,
        storyMapPermissionFromDetail,
        testRunPermissionFromDetail,
        userProfile?._id,
    ])

    const isStoryMapAccessPending = useMemo(() => {
        if (isWidgetMode) return false
        if (loginStatus === 'loading') return true
        if (canBypassStoryMapPermissions) return false
        if (storyMapPermission) return false

        const record = resolveProjectDetailRecord(projectDetail)
        const isProjectReady =
            projectDetailStatus === 'loaded' &&
            record?._id &&
            String(record._id) === String(projectId)

        if (projectDetailStatus === 'loading' || !isProjectReady) return true
        if (isClientUser && !clientStoryMapPermissionsResolved) return true

        return false
    }, [
        isWidgetMode,
        loginStatus,
        canBypassStoryMapPermissions,
        storyMapPermission,
        projectDetail,
        projectDetailStatus,
        projectId,
        isClientUser,
        clientStoryMapPermissionsResolved,
    ])

    const canViewStoryMap = useMemo(() => {
        if (canBypassStoryMapPermissions) return true
        if (storyMapPermission) return Boolean(storyMapPermission.isRead)
        return false
    }, [canBypassStoryMapPermissions, storyMapPermission])

    const canAccessStoryMapRoute = canViewStoryMap || isStoryMapAccessPending

    const canViewTestRun = useMemo(() => {
        if (canBypassStoryMapPermissions) return true
        if (testRunPermission) return Boolean(testRunPermission.isRead)
        if (projectDetailStatus === 'loading') return false
        if (!resolveProjectDetailRecord(projectDetail)?._id) return false
        return false
    }, [
        canBypassStoryMapPermissions,
        projectDetail,
        projectDetailStatus,
        testRunPermission,
    ])

    const storyMapReadOnly = useMemo(() => {
        if (canBypassStoryMapPermissions) return false
        if (!storyMapPermission) return true
        return !Boolean(storyMapPermission.isEdit)
    }, [canBypassStoryMapPermissions, storyMapPermission])

    const storyMapCanDelete = useMemo(() => {
        if (canBypassStoryMapPermissions) return true
        if (!storyMapPermission) return false
        return Boolean(storyMapPermission.isDelete)
    }, [canBypassStoryMapPermissions, storyMapPermission])

    const swimLaneOptions = useMemo(() => {
        const lanes = projectDetail?.supportSwimlanes || []
        return lanes.map((data) => ({
            label: data?.name,
            value: data?._id,
            color: data?.color || 'cyan',
        }))
    }, [projectDetail?.supportSwimlanes])

    const isProjectAccessDenied = projectDetailStatus === 'access_denied'

    const getProjectDetail = async () => {
        console.log('getProjectDetail', routeId, isWidgetMode)
        if (!routeId) return

        let loadProjectId = routeId

        if (isWidgetMode) {
            const widgetCode = resolveWidgetCodeForRoute(
                routeId,
                location.search
            )
            if (!widgetCode) {
                setWidgetAuthStatus('error')
                setWidgetResolvedProjectId(null)
                return
            }

            setWidgetAuthStatus('loading')
            let tokenPayload
            try {
                tokenPayload = await refreshWidgetTokenFromApi(widgetCode)
            } catch {
                setWidgetAuthStatus('error')
                setWidgetResolvedProjectId(null)
                return
            }
            if (!tokenPayload?.projectId) {
                setWidgetAuthStatus('error')
                setWidgetResolvedProjectId(null)
                return
            }
            setWidgetResolvedProjectId(tokenPayload.projectId)
            loadProjectId = tokenPayload.projectId

            const widgetPayload = await getProjectByWidgetCode({ widgetCode })
            if (!widgetPayload?.success || !widgetPayload?.result?._id) {
                setWidgetAuthStatus('error')
                setWidgetResolvedProjectId(null)
                return
            }

            const targetView = view || 'board'
            if (routeId !== loadProjectId) {
                // Stay on loading until the remounted route finishes fetching.
                navigate(
                    {
                        pathname: PRIVATE_ROUTES.projects.projectIdView(
                            loadProjectId,
                            targetView
                        ),
                        search: buildProjectWidgetSearch(widgetCode),
                    },
                    { replace: true }
                )
                return
            }
        }

        const result = await dispatch(fetchProjectDetail({ id: loadProjectId }))
        const data = result?.payload?.data

        if (isWidgetMode) {
            setWidgetAuthStatus('loaded')
        }

        if (
            data?.code === 'PROJECT_ACCESS_DENIED' ||
            (data?.success === false && data?.code === 'PROJECT_ACCESS_DENIED')
        ) {
            return
        }

        dispatch(
            taskListSupport({
                skip: 0,
                limit: 10,
                filter: {
                    projectId: loadProjectId,
                },
                listType: 'status',
            })
        )
    }

    useEffect(() => {
        if (routeId) {
            getProjectDetail()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, routeId, isWidgetMode])

    useEffect(() => {
        if (!isWidgetMode) {
            clearWidgetSession()
            setWidgetAuthStatus('idle')
            setWidgetResolvedProjectId(null)
        }
    }, [isWidgetMode])

    useEffect(() => {
        developmentToolbarFilterRef.current = {}
        supportToolbarFilterRef.current = {}
        setSupportListQuery({
            isCompleted: null,
            mineAssignee: null,
        })
        setTaskListSearchTerm('')
    }, [projectId])

    useEffect(() => {
        if (!routeId || isProjectAccessDenied) return
        if (isWidgetMode && !projectId) return

        if (!view) {
            navigate(
                {
                    pathname: PRIVATE_ROUTES.projects.projectIdView(
                        routeId,
                        'board'
                    ),
                    search: widgetSearch,
                },
                { replace: true }
            )
            return
        }

        const taskTab = readTaskTabQuery(location.search)
        const isDevelopmentRoute =
            !isWidgetMode &&
            taskTab === PROJECT_TASK_TAB_DEVELOPMENT &&
            canAccessDevelopmentTab
        const topLevelViewRoutes = TOP_LEVEL_VIEW_ROUTES.filter((routeView) => {
            if (routeView === 'storyMap') return canAccessStoryMapRoute
            if (routeView === 'testrun') return canViewTestRun
            return true
        })
        const validViews = isDevelopmentRoute
            ? DEVELOPMENT_VIEW_ROUTES
            : [...SUPPORT_VIEW_ROUTES, ...topLevelViewRoutes]

        const routeSearch = resolveProjectRouteSearch(
            isWidgetMode,
            widgetSearch,
            location.search
        )

        if (!validViews.includes(view)) {
            navigate(
                {
                    pathname: PRIVATE_ROUTES.projects.projectIdView(
                        routeId,
                        'board'
                    ),
                    search: routeSearch,
                },
                { replace: true }
            )
            return
        }

        if (view === 'storyMap' && !canAccessStoryMapRoute) {
            navigate(
                {
                    pathname: PRIVATE_ROUTES.projects.projectIdView(
                        routeId,
                        'board'
                    ),
                    search: routeSearch,
                },
                { replace: true }
            )
            return
        }

        if (view === 'testrun' && !canViewTestRun) {
            navigate(
                {
                    pathname: PRIVATE_ROUTES.projects.projectIdView(
                        routeId,
                        'board'
                    ),
                    search: widgetSearch,
                },
                { replace: true }
            )
            return
        }

        if (
            view === 'files' ||
            view === 'reports' ||
            view === 'storyMap' ||
            view === 'testrun'
        ) {
            setActiveMainTab((prev) => (prev === view ? prev : view))
        } else if (
            !isWidgetMode &&
            taskTab === PROJECT_TASK_TAB_DEVELOPMENT &&
            canAccessDevelopmentTab
        ) {
            setActiveMainTab('development')
            const devView =
                view === 'list' || view === 'roadmap' || view === 'sprint'
                    ? view
                    : 'board'
            setDevelopmentSubView((prev) => (prev === devView ? prev : devView))
        } else if (canViewSupportTab) {
            setActiveMainTab((prev) => (prev === 'support' ? prev : 'support'))
            setSupportSubView((prev) => (prev === view ? prev : view))
        } else if (canAccessDevelopmentTab && !isWidgetMode) {
            setActiveMainTab('development')
        } else if (canViewStoryMap) {
            setActiveMainTab('storyMap')
        } else {
            setActiveMainTab('files')
        }
    }, [
        routeId,
        projectId,
        view,
        navigate,
        location.search,
        widgetSearch,
        isWidgetMode,
        canAccessDevelopmentTab,
        canAccessStoryMapRoute,
        canViewTestRun,
        canViewSupportTab,
        canViewStoryMap,
        isProjectAccessDenied,
    ])

    useEffect(() => {
        if (isProjectAccessDenied || canViewStoryMap || isStoryMapAccessPending)
            return
        if (activeMainTab !== 'storyMap') return

        setActiveMainTab('support')
        navigate(
            {
                pathname: PRIVATE_ROUTES.projects.projectIdView(
                    routeId,
                    'board'
                ),
                search: resolveProjectRouteSearch(
                    isWidgetMode,
                    widgetSearch,
                    location.search
                ),
            },
            { replace: true }
        )
    }, [
        activeMainTab,
        canViewStoryMap,
        isStoryMapAccessPending,
        isProjectAccessDenied,
        navigate,
        routeId,
        widgetSearch,
        isWidgetMode,
        location.search,
    ])

    useEffect(() => {
        if (isProjectAccessDenied || canViewTestRun) return
        if (activeMainTab !== 'testrun') return

        setActiveMainTab('support')
        navigate(
            {
                pathname: PRIVATE_ROUTES.projects.projectIdView(
                    routeId,
                    'board'
                ),
                search: widgetSearch,
            },
            { replace: true }
        )
    }, [
        activeMainTab,
        canViewTestRun,
        isProjectAccessDenied,
        navigate,
        routeId,
        widgetSearch,
    ])

    useEffect(() => {
        if (!projectId || activeMainTab !== 'testrun') return undefined
        const previous = localStorage.getItem('currentProject')
        localStorage.setItem(
            'currentProject',
            JSON.stringify({ id: projectId })
        )
        return () => {
            if (previous) localStorage.setItem('currentProject', previous)
            else localStorage.removeItem('currentProject')
        }
    }, [projectId, activeMainTab])
    useEffect(() => {
        if (isProjectAccessDenied) return
        if (view === 'testrun' && taskId) {
            setActiveMainTab('testrun')
            setTaskDetailVisible(false)
            setTaskDetailFullscreen(false)
            setFullscreenAnimState('idle')
            setSelectedTaskId(null)
            setScrollToSection(null)
            return
        }
        if (taskId) {
            setSelectedTaskId(taskId)
            setTaskDetailVisible(true)
            return
        }

        setTaskDetailVisible(false)
        setTaskDetailFullscreen(false)
        setFullscreenAnimState('idle')
        setSelectedTaskId(null)
        setScrollToSection(null)
    }, [taskId, view, isProjectAccessDenied])

    useEffect(() => {
        if (isProjectAccessDenied) return
        if (
            (isWidgetMode || !canAccessDevelopmentTab) &&
            activeMainTab === 'development'
        ) {
            setActiveMainTab('support')
        }
    }, [
        activeMainTab,
        canAccessDevelopmentTab,
        isProjectAccessDenied,
        isWidgetMode,
    ])

    // Support module guard: kick the user off the Support tab when isRead is false.
    useEffect(() => {
        if (isProjectAccessDenied || canViewSupportTab) return
        if (activeMainTab !== 'support') return

        const fallbackTab =
            canAccessDevelopmentTab && !isWidgetMode
                ? 'development'
                : canViewStoryMap
                  ? 'storyMap'
                  : 'files'
        setActiveMainTab(fallbackTab)
        const fallbackView =
            fallbackTab === 'development' ? developmentSubView : fallbackTab
        navigate(
            {
                pathname: PRIVATE_ROUTES.projects.projectIdView(
                    routeId,
                    fallbackView
                ),
                search: mergeProjectRouteSearch(
                    isWidgetMode,
                    widgetSearch,
                    fallbackTab === 'development'
                        ? buildProjectTaskTabSearch('development')
                        : ''
                ),
            },
            { replace: true }
        )
    }, [
        activeMainTab,
        canViewSupportTab,
        canAccessDevelopmentTab,
        canViewStoryMap,
        developmentSubView,
        isProjectAccessDenied,
        isWidgetMode,
        navigate,
        routeId,
        widgetSearch,
    ])

    useEffect(() => {
        if (isMemberModalVisible) {
            // Fetch users for invite dropdown
            dispatch(resetClientModalUsers())
            dispatch(
                usersListForClientModal({
                    skip: 0,
                    filter: {},
                    limit: 10,
                    search: '',
                    projectId,
                })
            )
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, isMemberModalVisible, projectId])

    const getProjectColor = (projectName) => {
        const colors = [
            '#60a5fa',
            '#34d399',
            '#a78bfa',
            '#f472b6',
            '#fb923c',
            '#fbbf24',
            '#4ade80',
            '#22d3ee',
        ]

        if (!projectName) return colors[0]

        let hash = 0
        for (let i = 0; i < projectName.length; i++) {
            hash = projectName.charCodeAt(i) + ((hash << 5) - hash)
        }

        return colors[Math.abs(hash) % colors.length]
    }

    const getMemberColor = (memberName) => {
        const colors = [
            '#60a5fa',
            '#34d399',
            '#a78bfa',
            '#f472b6',
            '#fb923c',
            '#fbbf24',
            '#4ade80',
            '#22d3ee',
        ]

        if (!memberName) return colors[0]

        let hash = 0
        for (let i = 0; i < memberName.length; i++) {
            hash = memberName.charCodeAt(i) + ((hash << 5) - hash)
        }

        return colors[Math.abs(hash) % colors.length]
    }

    const getInitials = (fullName) => {
        if (!fullName) return ''
        const names = fullName.trim().split(/\s+/)
        if (names.length >= 2) {
            return (
                names[0].charAt(0).toUpperCase() +
                names[names.length - 1].charAt(0).toUpperCase()
            )
        } else if (names.length === 1) {
            return names[0].charAt(0).toUpperCase()
        }
        return ''
    }

    const resolveClientAvatarSrc = (profilePicture) => {
        if (!profilePicture) return undefined
        if (String(profilePicture).startsWith('file://')) return undefined
        return profilePicture
    }

    const getClientAvatarSrc = useCallback((client) => {
        const fromApi = resolveClientAvatarSrc(client?.profilePicture)
        if (fromApi) return fromApi
        return getRandomAvatar({
            gender: client?.gender,
            seed: client?._id || client?.email || client?.userName,
        })
    }, [])

    const projectClients = useMemo(
        () =>
            Array.isArray(projectDetail?.clients)
                ? projectDetail.clients.filter((c) => c?._id)
                : [],
        [projectDetail?.clients]
    )

    const PROJECT_CLIENTS_VISIBLE = 5
    const visibleProjectClients = useMemo(
        () => projectClients?.slice(0, PROJECT_CLIENTS_VISIBLE),
        [projectClients]
    )
    const hiddenProjectClientsCount = Math.max(
        0,
        projectClients?.length - PROJECT_CLIENTS_VISIBLE
    )

    const clientModalFallbackProjects = useMemo(
        () =>
            projectDetail?._id
                ? [
                      {
                          _id: projectDetail._id,
                          name: projectDetail.name || 'Current project',
                      },
                  ]
                : [],
        [projectDetail?._id, projectDetail?.name]
    )

    const handleEditProjectClient = useCallback(
        (client) => {
            if (!canEditProjectClient) return
            setClientModalMode('edit')
            setEditingClient(client || null)
            setIsClientModalOpen(true)
            setProjectClientsPopoverOpen(false)
        },
        [canEditProjectClient]
    )

    const handleClientModalClose = useCallback(() => {
        setIsClientModalOpen(false)
        setClientModalMode('create')
        setEditingClient(null)
        if (projectId) {
            dispatch(fetchProjectDetail({ id: projectId }))
        }
    }, [dispatch, projectId])

    const projectClientsPopoverContent = useMemo(
        () => (
            <div className={styles.projectClientsPopoverPanel}>
                <div className={styles.projectClientsPopoverHeader}>
                    <span className={styles.projectClientsPopoverTitle}>
                        Project clients
                    </span>
                    <span className={styles.projectClientsPopoverBadge}>
                        {projectClients.length}
                    </span>
                </div>
                <ul className={styles.projectClientsPopoverList}>
                    {projectClients.map((client) => {
                        const displayName = client?.userName || 'Unnamed client'
                        const avatarSrc = getClientAvatarSrc(client)

                        return (
                            <li
                                key={client._id}
                                className={styles.projectClientsPopoverItem}
                                {...(canEditProjectClient
                                    ? {
                                          role: 'button',
                                          tabIndex: 0,
                                          onClick: () =>
                                              handleEditProjectClient(client),
                                          onKeyDown: (event) => {
                                              if (
                                                  event.key === 'Enter' ||
                                                  event.key === ' '
                                              ) {
                                                  event.preventDefault()
                                                  handleEditProjectClient(
                                                      client
                                                  )
                                              }
                                          },
                                      }
                                    : { style: { cursor: 'default' } })}
                            >
                                <Avatar
                                    size={32}
                                    src={avatarSrc}
                                    className={
                                        styles.projectClientsPopoverAvatar
                                    }
                                    style={{
                                        backgroundColor:
                                            getMemberColor(displayName),
                                        fontSize: 12,
                                        fontWeight: 600,
                                        flexShrink: 0,
                                    }}
                                >
                                    {!avatarSrc
                                        ? getInitials(displayName)
                                        : null}
                                </Avatar>
                                <div
                                    className={styles.projectClientsPopoverMeta}
                                >
                                    <span
                                        className={
                                            styles.projectClientsPopoverName
                                        }
                                    >
                                        {displayName}
                                    </span>
                                    {client?.email ? (
                                        <span
                                            className={
                                                styles.projectClientsPopoverEmail
                                            }
                                        >
                                            {client.email}
                                        </span>
                                    ) : null}
                                </div>
                            </li>
                        )
                    })}
                </ul>
            </div>
        ),
        [
            projectClients,
            getClientAvatarSrc,
            handleEditProjectClient,
            canEditProjectClient,
        ]
    )

    // Convert hex to rgba with opacity
    const hexToRgba = (hex, opacity) => {
        const r = parseInt(hex.slice(1, 3), 16)
        const g = parseInt(hex.slice(3, 5), 16)
        const b = parseInt(hex.slice(5, 7), 16)
        return `rgba(${r}, ${g}, ${b}, ${opacity})`
    }

    const handleCloseMemberModal = () => {
        setIsMemberModalVisible(false)
        setInviteEmail([])
        setSelectedMap({})
        setUserSearchValue('')
        dispatch(resetClientModalUsers())
    }

    // Debounced search for users
    const handleUserSearch = React.useMemo(
        () =>
            debounce((value) => {
                dispatch(setClientModalUserSearch(value))
                dispatch(
                    usersListForClientModal({
                        skip: 0,
                        filter: value ? { search: value } : {},
                        limit: 10,
                        search: value,
                        projectId,
                    })
                )
            }, 300),
        [dispatch, projectId]
    )

    // Load more users on scroll
    const handleUserScroll = (e) => {
        const { target } = e
        const scrollTop = target.scrollTop
        const scrollHeight = target.scrollHeight
        const clientHeight = target.clientHeight

        if (
            scrollHeight - scrollTop <= clientHeight + 50 &&
            clientModalUserIsNext &&
            getUserStatus !== 'loading'
        ) {
            const nextPage = clientModalUserPage + 1
            const skip = (nextPage - 1) * 10

            dispatch(
                usersListForClientModal({
                    skip: skip,
                    filter: clientModalUserSearch
                        ? { search: clientModalUserSearch }
                        : {},
                    limit: 10,
                    search: clientModalUserSearch,
                    projectId,
                })
            )
        }
    }

    const handleInvite = async () => {
        if (!inviteEmail || inviteEmail?.length === 0) {
            message.warning('Please select a user')
            return
        }

        setIsInviting(true)
        const invitedUserNames = inviteEmail
            ?.map((id) => selectedMap[id]?.textLabel || id)
            .join(', ')

        try {
            // Call API with array of userIds
            const result = await assignUserToProject({
                userIds: inviteEmail,
                projectId,
            })

            if (result?.data?.success !== false) {
                showActionNotification({
                    message: 'Invitation sent successfully!',
                    subMessage: invitedUserNames,
                })
                setInviteEmail([])
                setSelectedMap({})
                // Refresh project detail to get updated members list
                dispatch(fetchProjectDetail({ id: projectId }))
            } else {
                showActionNotification({
                    message: 'Failed to send invitations. Please try again.',
                    subMessage: invitedUserNames,
                })
            }
        } catch (error) {
            showActionNotification({
                message: 'Failed to send invitations. Please try again.',
                subMessage: invitedUserNames,
            })
        } finally {
            setIsInviting(false)
        }
    }

    const handleLeaveProject = (memberId, userName) => {
        Modal.confirm({
            title: 'Remove Member',
            content:
                'Are you sure you want to remove this member from the project?',
            okText: 'Leave',
            okType: 'danger',
            cancelText: 'Cancel',
            onOk: async () => {
                try {
                    // Call API with array of userIds
                    const result = await unAssignUserToProject({
                        userIds: [memberId],
                        projectId,
                    })

                    if (result?.data?.success !== false) {
                        showActionNotification({
                            message: 'Member removed successfully!',
                            subMessage: userName,
                        })
                        dispatch(fetchProjectDetail({ id: projectId }))
                    } else {
                        showActionNotification({
                            message:
                                'Failed to remove member. Please try again.',
                            subMessage: userName,
                        })
                    }
                } catch (error) {
                    showActionNotification({
                        message: 'Failed to remove member. Please try again.',
                        subMessage: userName,
                    })
                }
            },
        })
    }

    const handleCopyProjectLink = useCallback(async () => {
        const linkProjectId = projectDetail?._id || projectId
        if (!linkProjectId) {
            message.warning('Project link is not ready yet')
            return
        }
        const link = `${window.location.origin}${PRIVATE_ROUTES.projects.projectId(linkProjectId)}`
        try {
            const ok = await copyTextToClipboard(link)
            if (!ok) throw new Error('copy failed')
            showActionNotification({
                message: 'Project link copied',
                subMessage: link,
                notificationKey: 'project-link-copied',
            })
        } catch {
            showActionNotification({
                message: 'Failed to copy project link',
                subMessage: link,
                notificationKey: 'project-link-copy-failed',
            })
        }
    }, [projectId, projectDetail?._id])

    const handleTaskClick = (clickedTaskId, scrollTo) => {
        if (String(clickedTaskId || '').startsWith('pending-create:')) {
            return
        }
        setSelectedTaskId(clickedTaskId)
        setTaskDetailVisible(true)
        if (scrollTo) {
            setScrollToSection(scrollTo)
        } else {
            setScrollToSection(null)
        }

        const viewForRoute =
            activeMainTab === 'development'
                ? developmentSubView
                : supportSubView
        const search = mergeProjectRouteSearch(
            isWidgetMode,
            widgetSearch,
            activeMainTab === 'development'
                ? buildProjectTaskTabSearch('development')
                : ''
        )
        navigate(
            {
                pathname: PRIVATE_ROUTES.projects.projectIdViewTask(
                    routeId,
                    viewForRoute,
                    clickedTaskId
                ),
                search,
            },
            { replace: true }
        )
    }

    const handleToggleFullscreen = useCallback(() => {
        if (taskDetailFullscreen) {
            // Play exit animation first; styles flip in onAnimationEnd
            setFullscreenAnimState('exiting')
        } else {
            setTaskDetailFullscreen(true)
            setFullscreenAnimState('entering')
        }
    }, [taskDetailFullscreen])

    const handleFullscreenAnimationEnd = useCallback(() => {
        if (fullscreenAnimState === 'exiting') {
            setTaskDetailFullscreen(false)
        }
        setFullscreenAnimState('idle')
    }, [fullscreenAnimState])

    useEffect(() => {
        taskDetailDrawerWidthRef.current = taskDetailDrawerWidth
    }, [taskDetailDrawerWidth])

    const applyTaskDetailDrawerWidth = useCallback(
        (width) => {
            taskDetailDrawerWidthRef.current = width
            const drawer = taskDetailDrawerRef.current
            const main = taskDetailMainContentRef.current

            if (drawer && !taskDetailFullscreen) {
                drawer.style.width = `${width}px`
            }
            if (main && taskDetailVisible && !taskDetailFullscreen) {
                main.style.width = `calc(100% - ${width}px)`
            }
        },
        [taskDetailFullscreen, taskDetailVisible]
    )

    const handleTaskDetailResizeStart = useCallback(
        (event) => {
            if (taskDetailFullscreen) return
            event.preventDefault()
            event.stopPropagation()

            const drawer = taskDetailDrawerRef.current
            const main = taskDetailMainContentRef.current

            taskDetailResizeRef.current = {
                startX: event.clientX,
                startWidth: taskDetailDrawerWidthRef.current,
            }
            taskDetailPendingWidthRef.current = null

            drawer?.classList.add(styles.taskDetailDrawerResizing)
            main?.classList.add(styles.taskDetailMainResizing)
            document.body.classList.add('taskDetailResizeActive')

            const scheduleWidthApply = (width) => {
                taskDetailPendingWidthRef.current = width
                if (taskDetailResizeRafRef.current != null) return
                taskDetailResizeRafRef.current = requestAnimationFrame(() => {
                    taskDetailResizeRafRef.current = null
                    const nextWidth = taskDetailPendingWidthRef.current
                    if (nextWidth == null) return
                    applyTaskDetailDrawerWidth(nextWidth)
                })
            }

            const handleMouseMove = (moveEvent) => {
                const { startX, startWidth } = taskDetailResizeRef.current
                const nextWidth = Math.min(
                    TASK_DETAIL_DRAWER_MAX_WIDTH,
                    Math.max(
                        TASK_DETAIL_DRAWER_MIN_WIDTH,
                        startWidth + (startX - moveEvent.clientX)
                    )
                )
                scheduleWidthApply(nextWidth)
            }

            const handleMouseUp = () => {
                document.removeEventListener('mousemove', handleMouseMove)
                document.removeEventListener('mouseup', handleMouseUp)

                if (taskDetailResizeRafRef.current != null) {
                    cancelAnimationFrame(taskDetailResizeRafRef.current)
                    taskDetailResizeRafRef.current = null
                }

                const finalWidth =
                    taskDetailPendingWidthRef.current ??
                    taskDetailDrawerWidthRef.current
                applyTaskDetailDrawerWidth(finalWidth)
                setTaskDetailDrawerWidth(finalWidth)
                taskDetailPendingWidthRef.current = null

                drawer?.classList.remove(styles.taskDetailDrawerResizing)
                main?.classList.remove(styles.taskDetailMainResizing)
                document.body.classList.remove('taskDetailResizeActive')
                document.body.style.cursor = ''
                document.body.style.userSelect = ''
            }

            document.addEventListener('mousemove', handleMouseMove)
            document.addEventListener('mouseup', handleMouseUp)
            document.body.style.cursor = 'col-resize'
            document.body.style.userSelect = 'none'
        },
        [applyTaskDetailDrawerWidth, taskDetailFullscreen]
    )

    const handleTaskDetailClose = useCallback(() => {
        setTaskDetailVisible(false)
        setTaskDetailFullscreen(false)
        setFullscreenAnimState('idle')
        setSelectedTaskId(null)
        setScrollToSection(null)
        const viewForRoute =
            activeMainTab === 'development'
                ? developmentSubView
                : supportSubView
        const search = mergeProjectRouteSearch(
            isWidgetMode,
            widgetSearch,
            activeMainTab === 'development'
                ? buildProjectTaskTabSearch('development')
                : ''
        )
        navigate(
            {
                pathname: PRIVATE_ROUTES.projects.projectIdView(
                    routeId,
                    viewForRoute
                ),
                search,
            },
            { replace: true }
        )
    }, [
        activeMainTab,
        developmentSubView,
        routeId,
        isWidgetMode,
        widgetSearch,
        navigate,
        supportSubView,
    ])

    const handleMainTabChange = (nextTab) => {
        if (nextTab === 'development' && !canAccessDevelopmentTab) return
        if (nextTab === 'storyMap' && !canViewStoryMap) return
        if (nextTab === 'testrun' && !canViewTestRun) return
        if (nextTab === 'support' && !canViewSupportTab) return
        setActiveMainTab(nextTab)
        setTaskDetailVisible(false)
        setTaskDetailFullscreen(false)
        setFullscreenAnimState('idle')
        setSelectedTaskId(null)
        setScrollToSection(null)
        const targetView =
            nextTab === 'development'
                ? developmentSubView
                : nextTab === 'files' ||
                    nextTab === 'reports' ||
                    nextTab === 'storyMap' ||
                    nextTab === 'testrun'
                  ? nextTab
                  : supportSubView
        const search = mergeProjectRouteSearch(
            isWidgetMode,
            widgetSearch,
            nextTab === 'development'
                ? buildProjectTaskTabSearch('development')
                : ''
        )
        navigate(
            {
                pathname: PRIVATE_ROUTES.projects.projectIdView(
                    routeId,
                    targetView
                ),
                search,
            },
            { replace: true }
        )
    }

    const handleDevelopmentSubViewChange = (nextView) => {
        if (!DEVELOPMENT_VIEW_ROUTES.includes(nextView)) return
        setDevelopmentSubView(nextView)
        const search = mergeProjectRouteSearch(
            isWidgetMode,
            widgetSearch,
            buildProjectTaskTabSearch('development')
        )
        const pathname = taskId
            ? PRIVATE_ROUTES.projects.projectIdViewTask(
                  routeId,
                  nextView,
                  taskId
              )
            : PRIVATE_ROUTES.projects.projectIdView(routeId, nextView)
        navigate({ pathname, search }, { replace: true })
    }

    const handleSupportSubViewChange = (nextView) => {
        if (!SUPPORT_VIEW_ROUTES.includes(nextView)) return
        setSupportSubView(nextView)
        const pathname = taskId
            ? PRIVATE_ROUTES.projects.projectIdViewTask(
                  routeId,
                  nextView,
                  taskId
              )
            : PRIVATE_ROUTES.projects.projectIdView(routeId, nextView)
        navigate({ pathname, search: widgetSearch }, { replace: true })
    }

    const applyDevelopmentToolbarQuery = useCallback(
        (toolbarFilter = {}, { skip = 0 } = {}) => {
            if (!projectId) return

            developmentToolbarFilterRef.current = mergeToolbarFilter(
                developmentToolbarFilterRef.current,
                toolbarFilter
            )
            const mergedFilter = developmentToolbarFilterRef.current

            if (developmentSubView === 'sprint') {
                const boardQuery =
                    buildDevelopmentSprintListQuery(mergedFilter)
                const { search: _search, ...epicQuery } = boardQuery
                dispatch(
                    loadDevelopmentSprintBoard({
                        projectId,
                        epicQuery,
                        sprintQuery: boardQuery,
                    })
                )
                return
            }

            dispatch(
                mainTaskList(
                    buildMainTaskListRequest({
                        projectId,
                        toolbarFilter: mergedFilter,
                        search: mergedFilter.search,
                        skip,
                    })
                )
            )
        },
        [dispatch, projectId, developmentSubView]
    )

    const applySupportToolbarQuery = useCallback(
        (toolbarFilter = {}, { skip = 0 } = {}) => {
            if (!projectId) return

            supportToolbarFilterRef.current = mergeToolbarFilter(
                supportToolbarFilterRef.current,
                toolbarFilter
            )
            const filter = supportToolbarFilterRef.current
            const hasUnassigned =
                Array.isArray(filter.selectedAssignees) &&
                filter.selectedAssignees.includes('UNASSIGNED')
            const searchTerm = String(filter.search ?? '').trim()

            setSupportListQuery({
                isCompleted:
                    typeof filter.isCompleted === 'boolean'
                        ? filter.isCompleted
                        : null,
                mineAssignee: filter.mineAssignee || null,
            })

            dispatch(
                taskListSupport({
                    skip,
                    limit: 10,
                    filter: {
                        projectId,
                        ...(hasUnassigned
                            ? { isUnassigned: true }
                            : {
                                  assignee:
                                      filter.mineAssignee ||
                                      filter.selectedAssignees,
                              }),
                        status: filter.selectedStatus,
                        ...(filter.selectedType?.length === 1
                            ? { type: filter.selectedType[0] }
                            : filter.selectedType?.length > 1
                              ? { type: filter.selectedType }
                              : {}),
                        ...(typeof filter.isCompleted === 'boolean'
                            ? { isCompleted: filter.isCompleted }
                            : {}),
                    },
                    listType: 'status',
                    ...(searchTerm ? { search: searchTerm } : {}),
                })
            )
        },
        [dispatch, projectId]
    )

    // Refetch board/list (and sprint) whenever those tabs become active so
    // data stays fresh after creating/updating tasks in another view.
    useEffect(() => {
        if (
            !projectId ||
            isProjectAccessDenied ||
            activeMainTab !== 'development'
        ) {
            return
        }
        applyDevelopmentToolbarQuery()
    }, [
        projectId,
        activeMainTab,
        developmentSubView,
        isProjectAccessDenied,
        applyDevelopmentToolbarQuery,
    ])

    useEffect(() => {
        if (
            !projectId ||
            isProjectAccessDenied ||
            activeMainTab !== 'support'
        ) {
            return
        }
        applySupportToolbarQuery()
    }, [
        projectId,
        activeMainTab,
        supportSubView,
        isProjectAccessDenied,
        applySupportToolbarQuery,
    ])

    useEffect(() => {
        selectedTaskIdRef.current = selectedTaskId
    }, [selectedTaskId])

    const handleTaskUpdate = useCallback(() => {
        const currentTaskId = selectedTaskIdRef.current
        if (currentTaskId) {
            if (activeMainTab === 'development') {
                dispatch(mainTaskById({ _id: currentTaskId }))
            } else {
                dispatch(taskById({ _id: currentTaskId }))
            }
        }
        if (activeMainTab === 'development') {
            applyDevelopmentToolbarQuery()
        } else {
            applySupportToolbarQuery()
        }
    }, [
        activeMainTab,
        dispatch,
        applyDevelopmentToolbarQuery,
        applySupportToolbarQuery,
    ])

    const handleTaskTitleLocalUpdate = useCallback((taskId, nextTitle) => {
        if (!taskId) return
        setLiveTaskTitlePatch({
            taskId,
            title: nextTitle,
            updatedAt: Date.now(),
        })
    }, [])

    const handleArchiveToggle = async () => {
        const archiveProjectId = projectDetail?._id || projectId
        if (!archiveProjectId) return

        const nextArchivedValue = !Boolean(projectDetail?.isArchived)
        const result = await dispatch(
            updateProject({
                _id: archiveProjectId,
                updatedData: {
                    isArchived: nextArchivedValue,
                },
            })
        )

        if (result?.payload?.data?.success) {
            showActionNotification({
                message: nextArchivedValue
                    ? 'Project archived successfully'
                    : 'Project unarchived successfully',
                subMessage: projectDetail?.name,
                notificationKey: nextArchivedValue
                    ? 'project-archived'
                    : 'project-unarchived',
            })
            dispatch(fetchProjectDetail({ id: archiveProjectId }))
        } else {
            showActionNotification({
                type: 'error',
                message: nextArchivedValue
                    ? 'Could not archive project'
                    : 'Could not unarchive project',
                subMessage:
                    result?.payload?.data?.message || 'Please try again.',
                notificationKey: 'project-archive-error',
            })
        }

        setProjectMenuOpen(false)
    }

    // Handle click outside task detail drawer to close it
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (!taskDetailVisible || !taskDetailDrawerRef.current) return

            // In fullscreen mode the drawer is position:fixed covering the entire
            // viewport — there is no "outside", so the close-on-outside-click
            // logic must be disabled. (This also prevents stale-closure issues
            // where contains() could return false for clicks on topBarActions.)
            if (taskDetailFullscreen) return

            // composedPath() pierces shadow-DOM boundaries (e.g. em-emoji-picker
            // web component), so we check both the retargeted event.target and
            // every element in the full composed path.
            const composedPath = event.composedPath ? event.composedPath() : []
            const isInsideDrawer =
                taskDetailDrawerRef.current.contains(event.target) ||
                composedPath.includes(taskDetailDrawerRef.current)

            if (!isInsideDrawer) {
                // Check if click is not on a modal or popover
                const isModal = event.target.closest?.('.ant-modal')
                const isPopover = event.target.closest?.('.ant-popover')
                const isDropdown = event.target.closest?.('.ant-dropdown')

                if (!isModal && !isPopover && !isDropdown) {
                    handleTaskDetailClose()
                }
            }
        }

        if (taskDetailVisible) {
            document.addEventListener('mousedown', handleClickOutside)
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
        }
    }, [taskDetailVisible, taskDetailFullscreen, handleTaskDetailClose])

    // Format user options from slice data
    const userOptions = React.useMemo(() => {
        if (clientModalUsersListData && clientModalUsersListData?.length > 0) {
            return clientModalUsersListData.map((user) => ({
                label: (
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                        }}
                    >
                        {user?.profilePicture ? (
                            <Avatar size={28} src={user?.profilePicture} />
                        ) : (
                            <Avatar
                                size={28}
                                style={{
                                    backgroundColor: getMemberColor(user?.name),
                                }}
                            >
                                {getInitials(user?.name)}
                            </Avatar>
                        )}
                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                            }}
                        >
                            <span style={{ fontSize: 13, fontWeight: 500 }}>
                                {user?.name}
                            </span>
                            <span style={{ fontSize: 12, color: '#64748b' }}>
                                {user?.email}
                            </span>
                        </div>
                    </div>
                ),
                value: user?._id,
                user: user,
                textLabel: user?.name || user?.email || 'Unnamed User',
                profilePicture: user?.profilePicture,
            }))
        }
        return []
    }, [clientModalUsersListData])

    const projectIconContrastColor = useMemo(() => {
        const pc =
            projectDetail?.colorCode || getProjectColor(projectDetail?.name)
        return harmonicIconToneForBackground(pc)
    }, [projectDetail])

    if (isWidgetMode && widgetAuthStatus === 'error') {
        return (
            <div className={styles.emptyState}>
                <Icon
                    icon="mdi:link-variant-off"
                    className={styles.emptyIcon}
                />
                <Text className={styles.emptyText}>Widget not found</Text>
                <Text className={styles.emptySubText}>
                    This embed link is invalid or no longer available.
                </Text>
            </div>
        )
    }

    if (
        isWidgetMode &&
        (widgetAuthStatus === 'idle' ||
            widgetAuthStatus === 'loading' ||
            projectDetailStatus === 'loading')
    ) {
        return (
            <div className={styles.loadingContainer}>
                <Icon
                    icon="line-md:loading-loop"
                    style={{ fontSize: 48, color: '#6366f1' }}
                />
            </div>
        )
    }

    if (projectDetailStatus === 'loading') {
        return (
            <div className={styles.loadingContainer}>
                <Icon
                    icon="line-md:loading-loop"
                    style={{ fontSize: 48, color: '#6366f1' }}
                />
            </div>
        )
    }

    if (isProjectAccessDenied) {
        return (
            <ProjectAccessDeniedView
                message={projectAccessDeniedMessage}
                activeProjectId={userProfile?.activeProjectId?._id}
                currentProjectId={routeId}
            />
        )
    }

    if (!projectDetail) {
        if (isWidgetMode) {
            return (
                <div className={styles.emptyState}>
                    <Icon
                        icon="mdi:link-variant-off"
                        className={styles.emptyIcon}
                    />
                    <Text className={styles.emptyText}>Widget not found</Text>
                    <Text className={styles.emptySubText}>
                        This embed link is invalid or no longer available.
                    </Text>
                </div>
            )
        }
        return (
            <div className={styles.emptyState}>
                <Icon
                    icon="mdi:folder-off-outline"
                    className={styles.emptyIcon}
                />
                <Text className={styles.emptyText}>Project not found</Text>
                <Button
                    type="primary"
                    onClick={() => navigate(-1)}
                    className={styles.backButton}
                >
                    Go Back
                </Button>
            </div>
        )
    }

    const projectColor =
        projectDetail?.colorCode || getProjectColor(projectDetail?.name)

    return (
        <ProjectPermissionsContext.Provider
            value={projectPermissionsContextValue}
        >
            <div
                className={styles.projectDetailContainer}
                id="project-detail-scroll"
            >
                <div
                    className={styles.projectChrome}
                    id="project-chrome"
                    data-project-chrome="true"
                >
                <div className={styles.projectInfo}>
                    <div className={styles.projectAvatarSection}>
                        <Popover
                            trigger={['click']}
                            open={avatarColorIconPopoverOpen}
                            onOpenChange={(open) => {
                                setAvatarColorIconPopoverOpen(open)
                                if (open) {
                                    setProjectMenuOpen(false)
                                    setColorIconPopoverOpen(false)
                                }
                            }}
                            placement="bottomLeft"
                            classNames={{
                                root: styles.colorIconPopoverOverlay,
                            }}
                            getPopupContainer={(triggerNode) =>
                                triggerNode?.parentElement || document.body
                            }
                            content={
                                <ProjectColorIconFlyout
                                    projectId={projectDetail?._id || projectId}
                                    palette={DEFAULT_COLOR_PALETTE}
                                    initialColor={projectDetail?.colorCode}
                                    onSaved={() =>
                                        setAvatarColorIconPopoverOpen(false)
                                    }
                                />
                            }
                            arrow={false}
                        >
                            <div
                                className={styles.projectDetailIcon}
                                style={{
                                    backgroundColor: projectColor,
                                    cursor: 'pointer',
                                }}
                                role="button"
                                tabIndex={0}
                                aria-label="Set project color & icon"
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault()
                                        setAvatarColorIconPopoverOpen(true)
                                    }
                                }}
                            >
                                {projectDetail?.urlType === 'icon' &&
                                projectDetail?.url ? (
                                    <Icon
                                        icon={projectDetail.url}
                                        width={18}
                                        height={18}
                                        style={{
                                            color: projectIconContrastColor,
                                        }}
                                    />
                                ) : projectDetail?.urlType === 'upload' &&
                                  projectDetail?.url ? (
                                    <img
                                        src={projectDetail.url}
                                        alt={projectDetail.name || ''}
                                        className={
                                            styles.projectDetailIconImage
                                        }
                                    />
                                ) : !projectDetail?.colorCode &&
                                  projectDetail?.projectProfile ? (
                                    <img
                                        src={projectDetail.projectProfile}
                                        alt={projectDetail.name || ''}
                                        className={
                                            styles.projectDetailIconImage
                                        }
                                    />
                                ) : !projectDetail?.colorCode ? (
                                    <Text
                                        className={styles.projectDetailIconText}
                                    >
                                        {projectDetail?.name
                                            ?.charAt(0)
                                            ?.toUpperCase()}
                                    </Text>
                                ) : null}
                            </div>
                        </Popover>
                    </div>
                    <div className={styles.projectDetails}>
                        <div className={styles.projectTitleRow}>
                            <Title level={4} className={styles.projectName}>
                                {projectDetail?.name || 'Unnamed Project'}
                            </Title>
                            {userProfile &&
                                (userProfile?.isSuperAdmin ||
                                    userProfile?.isExistingUser) && (
                                    <Dropdown
                                        trigger={['click']}
                                        open={projectMenuOpen}
                                        onOpenChange={(open) => {
                                            setProjectMenuOpen(open)
                                            if (!open)
                                                setColorIconPopoverOpen(false)
                                        }}
                                        placement="bottomLeft"
                                        overlayClassName={
                                            styles.projectMenuDropdown
                                        }
                                        popupRender={() => (
                                            <div
                                                className={
                                                    styles.customProjectMenu
                                                }
                                            >
                                                <button
                                                    type="button"
                                                    className={
                                                        styles.customProjectMenuItem
                                                    }
                                                    onClick={() => {
                                                        setEditProjectOpen(true)
                                                        setProjectMenuOpen(
                                                            false
                                                        )
                                                    }}
                                                >
                                                    <Icon
                                                        icon="mdi:pencil"
                                                        width={16}
                                                        height={16}
                                                        style={{
                                                            color: '#757677',
                                                            marginRight: 8,
                                                            flexShrink: 0,
                                                        }}
                                                    />
                                                    <span>Edit Project</span>
                                                </button>
                                                <Popover
                                                    trigger={['hover']}
                                                    open={colorIconPopoverOpen}
                                                    onOpenChange={
                                                        setColorIconPopoverOpen
                                                    }
                                                    placement="rightTop"
                                                    overlayClassName={
                                                        styles.colorIconPopoverOverlay
                                                    }
                                                    getPopupContainer={(
                                                        triggerNode
                                                    ) =>
                                                        triggerNode?.closest?.(
                                                            '.ant-dropdown'
                                                        ) || document.body
                                                    }
                                                    content={
                                                        <ProjectColorIconFlyout
                                                            projectId={
                                                                projectDetail?._id ||
                                                                projectId
                                                            }
                                                            palette={
                                                                DEFAULT_COLOR_PALETTE
                                                            }
                                                            initialColor={
                                                                projectDetail?.colorCode
                                                            }
                                                            onSaved={() =>
                                                                setProjectMenuOpen(
                                                                    false
                                                                )
                                                            }
                                                        />
                                                    }
                                                    arrow={false}
                                                >
                                                    <button
                                                        type="button"
                                                        className={`${styles.customProjectMenuItem} ${styles.customProjectMenuItemRow}`}
                                                        onClick={(e) =>
                                                            e.stopPropagation()
                                                        }
                                                    >
                                                        <span
                                                            className={
                                                                styles.menuColorDot
                                                            }
                                                            style={{
                                                                backgroundColor:
                                                                    projectDetail?.colorCode ||
                                                                    projectColor,
                                                                width: 16,
                                                                height: 16,
                                                            }}
                                                            aria-hidden
                                                        />
                                                        <span
                                                            style={{
                                                                flex: 1,
                                                                textAlign:
                                                                    'left',
                                                            }}
                                                        >
                                                            Set color & icon
                                                        </span>
                                                        <Icon
                                                            icon="mdi:chevron-right"
                                                            width={18}
                                                            height={18}
                                                            style={{
                                                                color: '#94a3b8',
                                                                flexShrink: 0,
                                                            }}
                                                        />
                                                    </button>
                                                </Popover>
                                                <button
                                                    type="button"
                                                    className={
                                                        styles.customProjectMenuItem
                                                    }
                                                    onClick={
                                                        handleCopyProjectLink
                                                    }
                                                >
                                                    <Icon
                                                        icon="qlementine-icons:link-16"
                                                        width={16}
                                                        height={16}
                                                        style={{
                                                            color: '#757677',
                                                            marginRight: 8,
                                                            flexShrink: 0,
                                                        }}
                                                    />
                                                    <span>
                                                        Copy project link
                                                    </span>
                                                </button>
                                                <button
                                                    type="button"
                                                    className={
                                                        styles.customProjectMenuItem
                                                    }
                                                    onClick={() => {
                                                        setImportModalVisible(
                                                            true
                                                        )
                                                        setProjectMenuOpen(
                                                            false
                                                        )
                                                    }}
                                                >
                                                    <Icon
                                                        icon="hugeicons:file-import"
                                                        width={16}
                                                        height={16}
                                                        style={{
                                                            color: '#757677',
                                                            marginRight: 8,
                                                            flexShrink: 0,
                                                        }}
                                                    />
                                                    <span>Import</span>
                                                </button>
                                                <button
                                                    type="button"
                                                    className={
                                                        styles.customProjectMenuItem
                                                    }
                                                    onClick={
                                                        handleArchiveToggle
                                                    }
                                                >
                                                    <Icon
                                                        icon={
                                                            'fluent:archive-20-regular'
                                                        }
                                                        width={16}
                                                        height={16}
                                                        style={{
                                                            color: '#757677',
                                                            marginRight: 8,
                                                            flexShrink: 0,
                                                        }}
                                                    />
                                                    <span>
                                                        {projectDetail?.isArchived
                                                            ? 'Unarchive Project'
                                                            : 'Archive Project'}
                                                    </span>
                                                </button>
                                            </div>
                                        )}
                                    >
                                        <Icon
                                            icon="mdi:chevron-down"
                                            width={20}
                                            height={20}
                                            style={{
                                                cursor: 'pointer',
                                                color: '#64748b',
                                                transition: 'transform 0.2s',
                                                transform: projectMenuOpen
                                                    ? 'rotate(180deg)'
                                                    : 'rotate(0deg)',
                                            }}
                                            onClick={(e) => {
                                                e.stopPropagation()
                                                setProjectMenuOpen(
                                                    !projectMenuOpen
                                                )
                                            }}
                                        />
                                    </Dropdown>
                                )}
                            {projectClients?.length > 0 ? (
                                <>
                                    <span
                                        className={styles.projectTitleDivider}
                                        aria-hidden
                                    />
                                    <Popover
                                        trigger={['click']}
                                        placement="bottomLeft"
                                        arrow={false}
                                        open={projectClientsPopoverOpen}
                                        onOpenChange={
                                            setProjectClientsPopoverOpen
                                        }
                                        overlayClassName={
                                            styles.projectClientsPopover
                                        }
                                        content={projectClientsPopoverContent}
                                    >
                                        <button
                                            type="button"
                                            className={
                                                styles.projectClientsPill
                                            }
                                            aria-label={`${projectClients.length} project clients. Click to view all.`}
                                        >
                                            <span
                                                className={
                                                    styles.projectClientsCompactLabel
                                                }
                                            >
                                                Clients
                                            </span>
                                            <span
                                                className={
                                                    styles.projectClientsCountBadge
                                                }
                                            >
                                                {projectClients.length}
                                            </span>
                                            <div
                                                className={
                                                    styles.projectClientsAvatars
                                                }
                                            >
                                                {visibleProjectClients.map(
                                                    (client) => {
                                                        const displayName =
                                                            client?.userName ||
                                                            'Unnamed client'
                                                        const avatarSrc =
                                                            getClientAvatarSrc(
                                                                client
                                                            )
                                                        const tip = displayName

                                                        return (
                                                            <Tooltip
                                                                key={client._id}
                                                                title={tip}
                                                                mouseEnterDelay={
                                                                    0.35
                                                                }
                                                            >
                                                                <Avatar
                                                                    size={26}
                                                                    src={
                                                                        avatarSrc
                                                                    }
                                                                    className={
                                                                        styles.projectClientAvatarChip
                                                                    }
                                                                    style={{
                                                                        backgroundColor:
                                                                            getMemberColor(
                                                                                displayName
                                                                            ),
                                                                        fontSize: 10,
                                                                        fontWeight: 600,
                                                                    }}
                                                                >
                                                                    {!avatarSrc
                                                                        ? getInitials(
                                                                              displayName
                                                                          )
                                                                        : null}
                                                                </Avatar>
                                                            </Tooltip>
                                                        )
                                                    }
                                                )}
                                                {hiddenProjectClientsCount >
                                                0 ? (
                                                    <span
                                                        className={
                                                            styles.projectClientsMoreChip
                                                        }
                                                        aria-hidden
                                                    >
                                                        +
                                                        {
                                                            hiddenProjectClientsCount
                                                        }
                                                    </span>
                                                ) : null}
                                            </div>
                                            <Icon
                                                icon="mdi:chevron-down"
                                                className={
                                                    styles.projectClientsChevron
                                                }
                                                width={16}
                                                height={16}
                                                aria-hidden
                                            />
                                        </button>
                                    </Popover>
                                </>
                            ) : null}
                        </div>
                    </div>
                    {/* <div className={styles.membersAvatars}>
                    {projectDetail?.members &&
                        projectDetail.members.length > 0 && (
                            <Avatar.Group
                                max={{
                                    count: 5,
                                    popover: {
                                        trigger: [],
                                    },
                                    style: {
                                        backgroundColor: '#6366f1',
                                        cursor: 'pointer',
                                        color: '#ffffff',
                                    },
                                }}
                                size={32}
                            >
                                {projectDetail.members.map((member) => {
                                    const memberColor = getMemberColor(
                                        member?.userName
                                    )
                                    const initials = getInitials(
                                        member?.userName
                                    )

                                    return (
                                        <Avatar
                                            key={member?._id}
                                            size={32}
                                            className={styles.memberAvatarGroup}
                                            src={member?.profilePicture}
                                            style={{
                                                backgroundColor: memberColor,
                                                fontSize: 12,
                                            }}
                                            onClick={() =>
                                                handleMemberClick(member)
                                            }
                                        >
                                            {initials || (
                                                <Icon
                                                    icon="mdi:account"
                                                    style={{ fontSize: 12 }}
                                                />
                                            )}
                                        </Avatar>
                                    )
                                })}
                            </Avatar.Group>
                        )}
                    <Tooltip title="Assign member">
                        <button
                            className={styles.moreOptionsButton}
                            onClick={() => handleMemberClick()}
                        >
                            <Icon
                                icon="mdi:dots-horizontal"
                                style={{ fontSize: 16 }}
                            />
                        </button>
                    </Tooltip>
                </div> */}
                </div>

                <div data-project-main-tabs>
                    <Tabs
                        activeKey={activeMainTab}
                        onChange={handleMainTabChange}
                        tabBarExtraContent={
                            <div className={styles.tabBarExtraWithScroll}>
                                <ProjectTabsScrollArrows rootSelector="[data-project-main-tabs]" />
                                {activeMainTab === 'development' ? (
                                    <div className={styles.tabBarSupportRow}>
                                        <div
                                            className={styles.supportSubTabs}
                                            role="tablist"
                                            aria-label="Development view"
                                        >
                                            <button
                                                type="button"
                                                role="tab"
                                                aria-selected={
                                                    developmentSubView ===
                                                    'board'
                                                }
                                                className={`${styles.supportSubTabBtn} ${
                                                    developmentSubView ===
                                                    'board'
                                                        ? styles.supportSubTabBtnActive
                                                        : ''
                                                }`}
                                                onClick={() =>
                                                    handleDevelopmentSubViewChange(
                                                        'board'
                                                    )
                                                }
                                            >
                                                <Icon
                                                    icon="si:bar-chart-line"
                                                    width={14}
                                                    style={{
                                                        transform:
                                                            'rotate(-180deg)',
                                                    }}
                                                />
                                                Board
                                            </button>
                                            <button
                                                type="button"
                                                role="tab"
                                                aria-selected={
                                                    developmentSubView ===
                                                    'list'
                                                }
                                                className={`${styles.supportSubTabBtn} ${
                                                    developmentSubView ===
                                                    'list'
                                                        ? styles.supportSubTabBtnActive
                                                        : ''
                                                }`}
                                                onClick={() =>
                                                    handleDevelopmentSubViewChange(
                                                        'list'
                                                    )
                                                }
                                            >
                                                <Icon
                                                    icon="carbon:list-boxes"
                                                    width={14}
                                                />
                                                List
                                            </button>
                                            <button
                                                type="button"
                                                role="tab"
                                                aria-selected={
                                                    developmentSubView ===
                                                    'roadmap'
                                                }
                                                className={`${styles.supportSubTabBtn} ${
                                                    developmentSubView ===
                                                    'roadmap'
                                                        ? styles.supportSubTabBtnActive
                                                        : ''
                                                }`}
                                                onClick={() =>
                                                    handleDevelopmentSubViewChange(
                                                        'roadmap'
                                                    )
                                                }
                                            >
                                                <Icon
                                                    icon="carbon:roadmap"
                                                    width={14}
                                                />
                                                Roadmap
                                            </button>
                                            <button
                                                type="button"
                                                role="tab"
                                                aria-selected={
                                                    developmentSubView ===
                                                    'sprint'
                                                }
                                                className={`${styles.supportSubTabBtn} ${
                                                    developmentSubView ===
                                                    'sprint'
                                                        ? styles.supportSubTabBtnActive
                                                        : ''
                                                }`}
                                                onClick={() =>
                                                    handleDevelopmentSubViewChange(
                                                        'sprint'
                                                    )
                                                }
                                            >
                                                <Icon
                                                    icon="mdi:run-fast"
                                                    width={14}
                                                />
                                                Sprint
                                            </button>
                                        </div>

                                        <>
                                            <span
                                                className={
                                                    styles.tabBarSupportDivider
                                                }
                                                aria-hidden
                                            />
                                            <ProjectTaskToolbar
                                                taskListKind="main"
                                                onAddTask={() => {
                                                    if (
                                                        addTaskToFirstSwimlaneRef.current
                                                    ) {
                                                        addTaskToFirstSwimlaneRef.current()
                                                    }
                                                }}
                                                onSearch={(value) => {
                                                    setTaskListSearchTerm(value)
                                                    applyDevelopmentToolbarQuery(
                                                        {
                                                            search: value,
                                                        }
                                                    )
                                                }}
                                                projectId={projectId}
                                                onFilterChange={(filter) => {
                                                    if (
                                                        filter &&
                                                        Object.prototype.hasOwnProperty.call(
                                                            filter,
                                                            'search'
                                                        )
                                                    ) {
                                                        setTaskListSearchTerm(
                                                            filter.search || ''
                                                        )
                                                    }
                                                    applyDevelopmentToolbarQuery(
                                                        filter
                                                    )
                                                }}
                                                activeViewTab={
                                                    developmentSubView
                                                }
                                            />
                                        </>
                                    </div>
                                ) : activeMainTab === 'support' &&
                                  canViewSupportTab ? (
                                    <div className={styles.tabBarSupportRow}>
                                        <div
                                            className={styles.supportSubTabs}
                                            role="tablist"
                                            aria-label="Support view"
                                        >
                                            <button
                                                type="button"
                                                role="tab"
                                                aria-selected={
                                                    supportSubView === 'board'
                                                }
                                                className={`${styles.supportSubTabBtn} ${
                                                    supportSubView === 'board'
                                                        ? styles.supportSubTabBtnActive
                                                        : ''
                                                }`}
                                                onClick={() =>
                                                    handleSupportSubViewChange(
                                                        'board'
                                                    )
                                                }
                                            >
                                                <Icon
                                                    icon="si:bar-chart-line"
                                                    width={14}
                                                    style={{
                                                        transform:
                                                            'rotate(-180deg)',
                                                    }}
                                                />
                                                Board
                                            </button>
                                            <button
                                                type="button"
                                                role="tab"
                                                aria-selected={
                                                    supportSubView === 'list'
                                                }
                                                className={`${styles.supportSubTabBtn} ${
                                                    supportSubView === 'list'
                                                        ? styles.supportSubTabBtnActive
                                                        : ''
                                                }`}
                                                onClick={() =>
                                                    handleSupportSubViewChange(
                                                        'list'
                                                    )
                                                }
                                            >
                                                <Icon
                                                    icon="carbon:list-boxes"
                                                    width={14}
                                                />
                                                List
                                            </button>
                                            <button
                                                type="button"
                                                role="tab"
                                                aria-selected={
                                                    supportSubView === 'roadmap'
                                                }
                                                className={`${styles.supportSubTabBtn} ${
                                                    supportSubView === 'roadmap'
                                                        ? styles.supportSubTabBtnActive
                                                        : ''
                                                }`}
                                                onClick={() =>
                                                    handleSupportSubViewChange(
                                                        'roadmap'
                                                    )
                                                }
                                            >
                                                <Icon
                                                    icon="carbon:roadmap"
                                                    width={14}
                                                />
                                                Roadmap
                                            </button>
                                        </div>
                                        <span
                                            className={
                                                styles.tabBarSupportDivider
                                            }
                                            aria-hidden
                                        />
                                        <ProjectTaskToolbar
                                            taskListKind="support"
                                            onAddTask={() => {
                                                if (
                                                    addTaskToFirstSwimlaneRef.current
                                                ) {
                                                    addTaskToFirstSwimlaneRef.current()
                                                }
                                            }}
                                            onSearch={(value) => {
                                                setTaskListSearchTerm(value)
                                                applySupportToolbarQuery({
                                                    search: value,
                                                })
                                            }}
                                            projectId={projectId}
                                            onFilterChange={(filter) => {
                                                if (
                                                    filter &&
                                                    Object.prototype.hasOwnProperty.call(
                                                        filter,
                                                        'search'
                                                    )
                                                ) {
                                                    setTaskListSearchTerm(
                                                        filter.search || ''
                                                    )
                                                }
                                                applySupportToolbarQuery(filter)
                                            }}
                                            activeViewTab={supportSubView}
                                        />
                                    </div>
                                ) : activeMainTab === 'storyMap' &&
                                  canAccessStoryMapRoute ? (
                                    <div className={styles.tabBarSupportRow}>
                                        <div
                                            {...{
                                                [STORY_MAP_TAB_BAR_HOST_ATTR]:
                                                    'true',
                                            }}
                                        >
                                            <div
                                                {...{
                                                    [STORY_MAP_TAB_BAR_SLOT_ATTR]:
                                                        'true',
                                                }}
                                            />
                                        </div>
                                    </div>
                                ) : null}
                            </div>
                        }
                        items={[
                            ...(canViewStoryMap
                                ? [
                                      {
                                          key: 'storyMap',
                                          label: (
                                              <span
                                                  className={styles.topTabLabel}
                                              >
                                                  <Icon
                                                      icon="mdi:map-marker-path"
                                                      width={16}
                                                      height={16}
                                                  />
                                                  Story Map
                                              </span>
                                          ),
                                      },
                                  ]
                                : []),
                            ...(canViewSupportTab
                                ? [
                                      {
                                          key: 'support',
                                          label: (
                                              <span
                                                  className={
                                                      styles.supportTabLabelWrap
                                                  }
                                              >
                                                  <span
                                                      className={
                                                          styles.topTabLabel
                                                      }
                                                  >
                                                      <Icon
                                                          icon="si:bar-chart-line"
                                                          width={16}
                                                          height={16}
                                                          style={{
                                                              transform:
                                                                  'rotate(-180deg)',
                                                          }}
                                                      />
                                                      Support
                                                  </span>
                                                  {canCopyWidgetLink &&
                                                  projectId ? (
                                                      <CopyWidgetLinkButton
                                                          projectId={projectId}
                                                      />
                                                  ) : null}
                                              </span>
                                          ),
                                      },
                                  ]
                                : []),
                            ...(canAccessDevelopmentTab
                                ? [
                                      {
                                          key: 'development',
                                          label: (
                                              <span
                                                  className={styles.topTabLabel}
                                              >
                                                  <Icon
                                                      icon="mdi:code-tags"
                                                      width={16}
                                                  />
                                                  Development
                                              </span>
                                          ),
                                      },
                                  ]
                                : []),
                            // {
                            //     key: 'testing',
                            //     label: (
                            //         <span className={styles.topTabLabel}>
                            //             <Icon icon="streamline:test-tube" width={16} />
                            //             Testing
                            //         </span>
                            //     ),
                            // },
                            {
                                key: 'files',
                                label: (
                                    <span className={styles.topTabLabel}>
                                        <Icon
                                            icon="codicon:files"
                                            width={16}
                                            height={16}
                                        />
                                        Files
                                    </span>
                                ),
                            },
                            ...(canViewTestRun
                                ? [
                                      {
                                          key: 'testrun',
                                          label: (
                                              <span
                                                  className={styles.topTabLabel}
                                              >
                                                  <Icon
                                                      icon="streamline:test-tube"
                                                      width={16}
                                                      height={16}
                                                  />
                                                  Test Run
                                              </span>
                                          ),
                                      },
                                  ]
                                : []),
                            // {
                            //     key: 'reports',
                            //     label: (
                            //         <span className={styles.topTabLabel}>
                            //             <Icon
                            //                 icon="codicon:files"
                            //                 width={16}
                            //                 height={16}
                            //             />
                            //             Reports
                            //         </span>
                            //     ),
                            // },
                        ]}
                        style={{
                            marginTop: '12px',
                        }}
                        tabBarStyle={{
                            padding: '0 24px',
                        }}
                        className={styles.viewTabs}
                    />
                </div>
                </div>

                <div
                    id="project-main-content"
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        minHeight: 0,
                        overflowY: 'hidden',
                        overflowX: 'hidden',
                        scrollbarColor: '#a0a1a1 #fffbfb',
                        flex: '1 1 0',
                        position: 'relative',
                        width:
                            taskDetailVisible && !taskDetailFullscreen
                                ? `calc(100% - ${taskDetailDrawerWidth}px)`
                                : '100%',
                    }}
                >
                    {/* Left Content Area - Horizontally Scrollable when drawer is open */}
                    {activeMainTab === 'support' &&
                        canViewSupportTab &&
                        supportSubView === 'board' && (
                            <ProjectTaskList
                                projectTaskList={projectTaskList}
                                projectTaskListStatus={projectTaskListStatus}
                                projectTaskListPage={projectTaskListPage}
                                projectTaskListIsNext={projectTaskListIsNext}
                                projectId={projectId}
                                onTaskClick={handleTaskClick}
                                taskDetailVisible={taskDetailVisible}
                                onAddTaskToFirstSwimlane={
                                    addTaskToFirstSwimlaneRef
                                }
                                taskRouteView={supportSubView}
                                liveTaskTitlePatch={liveTaskTitlePatch}
                                onRefreshTasks={applySupportToolbarQuery}
                                listQuery={supportListQuery}
                            />
                        )}
                    {activeMainTab === 'support' &&
                        canViewSupportTab &&
                        supportSubView === 'list' && (
                            <ProjectTaskListView
                                projectTaskList={projectTaskList}
                                projectTaskListStatus={projectTaskListStatus}
                                projectTaskListPage={projectTaskListPage}
                                projectTaskListIsNext={projectTaskListIsNext}
                                projectId={projectId}
                                onTaskClick={handleTaskClick}
                                taskDetailVisible={taskDetailVisible}
                                onAddTaskToFirstSwimlane={
                                    addTaskToFirstSwimlaneRef
                                }
                                liveTaskTitlePatch={liveTaskTitlePatch}
                                searchTerm={taskListSearchTerm}
                                onRefreshTasks={applySupportToolbarQuery}
                                listQuery={supportListQuery}
                            />
                        )}

                    {activeMainTab === 'support' &&
                        canViewSupportTab &&
                        supportSubView === 'roadmap' && (
                            <ProjectRoadMap
                                apiData={projectTaskList}
                                onTaskClick={handleTaskClick}
                                projectId={projectId}
                            />
                        )}

                    {activeMainTab === 'development' &&
                        developmentSubView === 'board' && (
                            <ProjectTaskList
                                taskListKind="main"
                                projectTaskList={projectMainTaskList}
                                projectTaskListStatus={
                                    projectMainTaskListStatus
                                }
                                projectTaskListPage={projectMainTaskListPage}
                                projectTaskListIsNext={
                                    projectMainTaskListIsNext
                                }
                                projectId={projectId}
                                onTaskClick={handleTaskClick}
                                taskDetailVisible={taskDetailVisible}
                                onAddTaskToFirstSwimlane={
                                    addTaskToFirstSwimlaneRef
                                }
                                taskRouteView={developmentSubView}
                                liveTaskTitlePatch={liveTaskTitlePatch}
                                onRefreshTasks={applyDevelopmentToolbarQuery}
                            />
                        )}
                    {activeMainTab === 'development' &&
                        developmentSubView === 'list' && (
                            <ProjectTaskListView
                                taskListKind="main"
                                projectTaskList={projectMainTaskList}
                                projectTaskListStatus={
                                    projectMainTaskListStatus
                                }
                                projectId={projectId}
                                onTaskClick={handleTaskClick}
                                taskDetailVisible={taskDetailVisible}
                                onAddTaskToFirstSwimlane={
                                    addTaskToFirstSwimlaneRef
                                }
                                liveTaskTitlePatch={liveTaskTitlePatch}
                                searchTerm={taskListSearchTerm}
                                onRefreshTasks={applyDevelopmentToolbarQuery}
                            />
                        )}
                    {activeMainTab === 'development' &&
                        developmentSubView === 'roadmap' && (
                            <ProjectRoadMap
                                apiData={projectMainTaskList}
                                onTaskClick={handleTaskClick}
                                projectId={projectId}
                                taskListKind="main"
                            />
                        )}
                    {activeMainTab === 'development' &&
                        developmentSubView === 'sprint' && (
                            <div
                                style={{
                                    flex: 1,
                                    minHeight: 0,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    overflow: 'hidden',
                                    width: '100%',
                                }}
                            >
                                <DevelopmentSprintView
                                    onTaskClick={handleTaskClick}
                                    projectId={projectId}
                                    filteredAssignees={filteredAssignees}
                                    onRefresh={applyDevelopmentToolbarQuery}
                                />
                            </div>
                        )}
                    {activeMainTab === 'reports' && (
                        <ReportsView
                            projectId={projectId}
                            projectName={projectDetail?.name}
                        />
                    )}

                    {activeMainTab === 'testrun' &&
                        canViewTestRun &&
                        !taskId && (
                            <div
                                style={{
                                    flex: 1,
                                    minHeight: 0,
                                    overflow: 'auto',
                                    width: '100%',
                                }}
                            >
                                <TestRunMain projectId={projectId} />
                            </div>
                        )}

                    {activeMainTab === 'testrun' &&
                        canViewTestRun &&
                        taskId && (
                            <div
                                style={{
                                    flex: 1,
                                    minHeight: 0,
                                    overflow: 'auto',
                                    width: '100%',
                                }}
                            >
                                <TestRunDetails
                                    reportId={taskId}
                                    projectId={projectId}
                                    backPath={PRIVATE_ROUTES.projects.projectIdView(
                                        routeId,
                                        'testrun'
                                    )}
                                />
                            </div>
                        )}

                    {activeMainTab === 'files' && (
                        <div
                            style={{
                                justifyContent: 'center',
                                alignItems: 'center',
                                height: '100%',
                                width: '100%',
                            }}
                        >
                            <ProjectFiles
                                projectId={projectId}
                                onTaskClick={handleTaskClick}
                            />
                        </div>
                    )}

                    {activeMainTab === 'storyMap' && canAccessStoryMapRoute && (
                        <div className={styles.storyMapTabHost}>
                            <ProjectStoryMap
                                projectId={projectId}
                                readOnly={storyMapReadOnly}
                                canDelete={storyMapCanDelete}
                            />
                        </div>
                    )}

                    {/* Drawer - Slides in from right */}
                </div>
                {activeMainTab === 'storyMap' ? (
                    <div
                        id="story-map-drawer-host"
                        className={styles.storyMapDrawerHost}
                    />
                ) : null}
                {(activeMainTab === 'support' ||
                    activeMainTab === 'development') && (
                    <div
                        ref={taskDetailDrawerRef}
                        className={
                            fullscreenAnimState === 'entering'
                                ? styles.taskDetailDrawerFullscreenEnter
                                : fullscreenAnimState === 'exiting'
                                  ? styles.taskDetailDrawerFullscreenExit
                                  : undefined
                        }
                        onAnimationEnd={
                            fullscreenAnimState !== 'idle'
                                ? handleFullscreenAnimationEnd
                                : undefined
                        }
                        style={
                            taskDetailFullscreen
                                ? {
                                      position: 'fixed',
                                      inset: 0,
                                      zIndex: 9999,
                                      backgroundColor: '#f1f5f9',
                                      display: 'flex',
                                      flexDirection: 'column',
                                      padding: '18px',
                                  }
                                : {
                                      position: 'absolute',
                                      top: '0px',
                                      right: 0,
                                      width: `${taskDetailDrawerWidth}px`,
                                      transform: taskDetailVisible
                                          ? 'translateX(0)'
                                          : 'translateX(100%)',
                                      transition:
                                          'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                      borderLeft: '1px solid #e2e8f0',
                                      backgroundColor: '#fff',
                                      boxShadow: 'none',
                                      zIndex: 2,
                                      height: '100%',
                                  }
                        }
                    >
                        {!taskDetailFullscreen && (
                            <div
                                role="separator"
                                aria-orientation="vertical"
                                aria-label="Resize task panel"
                                className={styles.taskDetailDrawerResizeHandle}
                                onMouseDown={handleTaskDetailResizeStart}
                            />
                        )}
                        <TaskDetailView
                            taskId={selectedTaskId}
                            visible={taskDetailVisible}
                            onClose={handleTaskDetailClose}
                            projectId={projectId}
                            filteredAssignees={filteredAssignees}
                            onTaskUpdate={handleTaskUpdate}
                            onTaskTitleLocalUpdate={handleTaskTitleLocalUpdate}
                            onTaskClick={handleTaskClick}
                            scrollToSection={scrollToSection}
                            detailSource={
                                activeMainTab === 'development'
                                    ? 'main'
                                    : 'support'
                            }
                            swimLaneOptions={swimLaneOptions}
                            projectTaskList={
                                activeMainTab === 'development'
                                    ? projectMainTaskList
                                    : projectTaskList
                            }
                            isFullscreen={taskDetailFullscreen}
                            onToggleFullscreen={handleToggleFullscreen}
                        />
                    </div>
                )}

                {/* Members Modal */}
                <Modal
                    title={null}
                    open={isMemberModalVisible}
                    onCancel={handleCloseMemberModal}
                    footer={null}
                    className={styles.memberModal}
                    width={520}
                    closeIcon={
                        <Icon icon="mdi:close" style={{ fontSize: 20 }} />
                    }
                >
                    <div className={styles.memberModalContent}>
                        {/* Modal Header */}
                        <div className={styles.modalHeader}>
                            <Title level={4} className={styles.modalTitle}>
                                Share {projectDetail?.name || 'Project'}
                            </Title>
                        </div>

                        {/* Invite Section */}
                        <div className={styles.inviteSection}>
                            <Select
                                mode="multiple"
                                value={inviteEmail}
                                removeIcon={
                                    <div
                                        style={{
                                            height: '14px',
                                            width: '14px',
                                        }}
                                    >
                                        <CloseModalIcon />
                                    </div>
                                }
                                placeholder="Add people..."
                                size="small"
                                filterOption={false}
                                searchValue={userSearchValue}
                                onSearch={(val) => {
                                    setUserSearchValue(val)
                                    handleUserSearch(val)
                                }}
                                autoClearSearchValue={false}
                                onPopupScroll={handleUserScroll}
                                notFoundContent={
                                    getUserStatus === 'loading' &&
                                    clientModalUserPage === 1 ? (
                                        <Icon
                                            icon="line-md:loading-loop"
                                            style={{
                                                fontSize: 24,
                                                color: '#6366f1',
                                            }}
                                        />
                                    ) : null
                                }
                                loading={
                                    getUserStatus === 'loading' &&
                                    clientModalUserPage === 1
                                }
                                onChange={(selectedValues, selectedOptions) => {
                                    setUserSearchValue('')
                                    handleUserSearch('')
                                    setInviteEmail(selectedValues)
                                    const updatedMap = { ...selectedMap }
                                    if (
                                        selectedOptions &&
                                        selectedOptions.length > 0
                                    ) {
                                        selectedOptions.forEach((sel) => {
                                            if (!updatedMap[sel.value]) {
                                                const match = userOptions.find(
                                                    (opt) =>
                                                        opt.value === sel.value
                                                )
                                                if (match)
                                                    updatedMap[sel.value] =
                                                        match
                                            }
                                        })
                                    } else {
                                        // Fallback: find from userOptions based on selectedValues
                                        selectedValues.forEach((val) => {
                                            if (!updatedMap[val]) {
                                                const match = userOptions.find(
                                                    (opt) => opt.value === val
                                                )
                                                if (match)
                                                    updatedMap[val] = match
                                            }
                                        })
                                    }
                                    // Remove unselected items
                                    Object.keys(updatedMap).forEach((key) => {
                                        if (!selectedValues.includes(key)) {
                                            delete updatedMap[key]
                                        }
                                    })
                                    setSelectedMap(updatedMap)
                                }}
                                tagRender={(props) => {
                                    const {
                                        closable,
                                        onClose,
                                        value: val,
                                    } = props
                                    const selectedUser = selectedMap[val]
                                    const memberColor = getMemberColor(
                                        selectedUser?.textLabel
                                    )
                                    const initialsBgColor = hexToRgba(
                                        memberColor,
                                        0.3
                                    )
                                    const initials = getInitials(
                                        selectedUser?.textLabel
                                    )

                                    return (
                                        <div className={styles.selectedUserTag}>
                                            {selectedUser?.profilePicture ? (
                                                <Avatar
                                                    size={18}
                                                    src={
                                                        selectedUser?.profilePicture
                                                    }
                                                    className={
                                                        styles.selectedUserAvatar
                                                    }
                                                />
                                            ) : (
                                                <div
                                                    className={
                                                        styles.selectedUserInitials
                                                    }
                                                    style={{
                                                        backgroundColor:
                                                            initialsBgColor,
                                                    }}
                                                >
                                                    {initials}
                                                </div>
                                            )}
                                            <span
                                                className={
                                                    styles.selectedUserName
                                                }
                                            >
                                                {selectedUser?.textLabel}
                                            </span>
                                            {closable && (
                                                <span
                                                    onClick={onClose}
                                                    className={
                                                        styles.selectedUserClose
                                                    }
                                                >
                                                    <CrossIcon
                                                        width={8}
                                                        color={'#64748b'}
                                                    />
                                                </span>
                                            )}
                                        </div>
                                    )
                                }}
                                options={userOptions}
                                className={styles.inviteSelect}
                                dropdownRender={(menu) => (
                                    <>
                                        {menu}
                                        {getUserStatus === 'loading' &&
                                            clientModalUserPage > 1 && (
                                                <div
                                                    className={
                                                        styles.loadingMore
                                                    }
                                                >
                                                    <Icon
                                                        icon="line-md:loading-loop"
                                                        style={{
                                                            fontSize: 24,
                                                            color: '#6366f1',
                                                        }}
                                                    />
                                                </div>
                                            )}
                                    </>
                                )}
                            />
                            <Button
                                type="primary"
                                size="large"
                                onClick={handleInvite}
                                disabled={
                                    !inviteEmail || inviteEmail?.length === 0
                                }
                                loading={isInviting}
                                className={styles.inviteButton}
                            >
                                Invite
                            </Button>
                        </div>

                        {/* Members List */}
                        <div className={styles.membersListSection}>
                            <Text className={styles.sectionTitle}>Members</Text>
                            <div className={styles.membersListContainer}>
                                {projectDetail?.members &&
                                projectDetail?.members?.length > 0 ? (
                                    projectDetail?.members?.map((member) => {
                                        const memberColor = getMemberColor(
                                            member?.userName
                                        )
                                        const initials = getInitials(
                                            member?.userName
                                        )

                                        return (
                                            <div
                                                key={member?._id}
                                                className={
                                                    styles.memberListItem
                                                }
                                            >
                                                <div
                                                    className={
                                                        styles.memberListInfo
                                                    }
                                                >
                                                    <Avatar
                                                        size={36}
                                                        className={
                                                            styles.memberListAvatar
                                                        }
                                                        src={
                                                            member?.profilePicture
                                                        }
                                                        style={{
                                                            backgroundColor:
                                                                memberColor,
                                                            fontSize: 14,
                                                        }}
                                                    >
                                                        {initials || (
                                                            <Icon
                                                                icon="mdi:account"
                                                                style={{
                                                                    fontSize: 14,
                                                                }}
                                                            />
                                                        )}
                                                    </Avatar>
                                                    <div
                                                        className={
                                                            styles.memberListDetails
                                                        }
                                                    >
                                                        <Text
                                                            className={
                                                                styles.memberListName
                                                            }
                                                        >
                                                            {member?.userName ||
                                                                'Unnamed Member'}
                                                        </Text>
                                                        {member?.email && (
                                                            <Text
                                                                className={
                                                                    styles.memberListEmail
                                                                }
                                                            >
                                                                {member?.email}
                                                            </Text>
                                                        )}
                                                    </div>
                                                </div>
                                                {!member?.isSuperAdmin &&
                                                    userProfile?._id !==
                                                        member?._id && (
                                                        <Button
                                                            type="text"
                                                            danger
                                                            icon={
                                                                <Icon
                                                                    icon="mdi:exit-to-app"
                                                                    style={{
                                                                        fontSize: 18,
                                                                    }}
                                                                />
                                                            }
                                                            onClick={() =>
                                                                handleLeaveProject(
                                                                    member?._id,
                                                                    member?.userName
                                                                )
                                                            }
                                                            className={
                                                                styles.leaveProjectButton
                                                            }
                                                        >
                                                            Remove from project
                                                        </Button>
                                                    )}
                                            </div>
                                        )
                                    })
                                ) : (
                                    <div className={styles.noMembers}>
                                        <Icon
                                            icon="mdi:account-group-outline"
                                            className={styles.noMembersIcon}
                                        />
                                        <Text>No members yet</Text>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </Modal>

                {/* Import Tasks Modal */}
                <ImportTasksModal
                    open={importModalVisible}
                    onCancel={() => setImportModalVisible(false)}
                    projectId={projectId}
                    onImportSuccess={() => {
                        // Refresh task list after successful import
                        dispatch(
                            taskListSupport({
                                skip: 0,
                                limit: 10,
                                filter: {
                                    projectId,
                                },
                                listType: 'status',
                            })
                        )
                    }}
                />

                <CreateClientModal
                    open={isClientModalOpen}
                    onCancel={handleClientModalClose}
                    mode={clientModalMode}
                    initialClient={editingClient}
                    fallbackProjects={clientModalFallbackProjects}
                />

                <CreateProjectModal
                    open={editProjectOpen}
                    onCancel={() => setEditProjectOpen(false)}
                    mode="edit"
                    projectId={projectDetail?._id || projectId}
                    initialProject={projectDetail}
                    onSaved={() => {
                        setEditProjectOpen(false)
                        dispatch(fetchProjectDetail({ id: projectId }))
                    }}
                />
            </div>
        </ProjectPermissionsContext.Provider>
    )
}

export default ProjectDetail
